import { useState } from 'react';
import CloudinaryService from '@/services/cloudinary/Cloudinary.service';
import SentryService from '@/services/sentry/Sentry.service';
import useStore from '@/stores/useStore';

type ImageChange = {
  newImage: string;
  oldImage?: string;
};

type ImageChanges = {
  [key: string]: ImageChange;
};

type UploadedImages = {
  [key: string]: string;
};

export function useImageUpload() {
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const setSnackbarState = useStore((state) => state.setSnackbarState);

  const uploadImage = async (
    token: string,
    imageData: string,
    oldImageUrl?: string | null,
  ): Promise<string> => {
    setIsUploading(true);
    try {
      // Skip upload if already a cloudinary URL
      if (imageData.includes('cloudinary')) {
        return imageData;
      }

      const uploadResult = await CloudinaryService.uploadToCloudinary(
        token,
        imageData,
      );

      // Delete old image if it exists and is a cloudinary URL
      if (oldImageUrl && oldImageUrl.includes('cloudinary')) {
        await CloudinaryService.deleteFromCloudinary(token, oldImageUrl).catch(
          (error: Error) => {
            SentryService.captureError(
              `Failed to delete image: ${oldImageUrl}`,
              error,
            );
          },
        );
      }

      return uploadResult.secure_url;
    } catch (error) {
      setSnackbarState({
        open: true,
        message: 'Error uploading image. Please try again.',
        severity: 'error',
      });
      SentryService.captureError('Error uploading image', error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const uploadMultipleImages = async (
    token: string,
    imageChanges: ImageChanges,
  ): Promise<UploadedImages> => {
    const results: UploadedImages = {};
    setIsUploading(true);

    try {
      await Promise.all(
        Object.entries(imageChanges).map(
          async ([key, { newImage, oldImage }]) => {
            results[key] = await uploadImage(token, newImage, oldImage);
          },
        ),
      );
      return results;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    isUploading,
    uploadImage,
    uploadMultipleImages,
  };
}
